<script lang="ts">
  import PersonalTools from '../PersonalTools.svelte'
  import type { OpsPayload } from '../../../shared/contract'
  import Groomed from './Groomed.svelte'
  import { filterTitle, filtering, liftsLine, perArea, type Filter, type Mountain } from '../tally'
  import { clockTime, freshnessParts, hourText, type Opening } from '../when'

  /**
   * Like wx: the time, a quiet freshness line, an hours lamp and one light
   * headline. With a filter on, the headline is the match as a sentence.
   */
  let {
    data,
    m,
    now,
    quiet,
    live = true,
    opens,
    filter,
    matched,
    openMode = false,
    lamps = false,
    liftsShown,
    onclear,
  }: {
    data: OpsPayload
    m: Mountain
    now: Date
    /** One quiet word for the freshness line ("offline"). */
    quiet?: string
    /** This session has heard from the edge: the line may say Live. */
    live?: boolean
    /** Opening day, while it is still ahead and nothing is turning. */
    opens: Opening | null
    filter: Filter
    matched: number
    /** An area is open on a phone: short screens give the headline's room to the list. */
    openMode?: boolean
    /** The plates show lamps without lift names: this header names the shorter list. */
    lamps?: boolean
    /** How many lift names fit on the lifts line; the rest become "+N more". */
    liftsShown?: number
    onclear: () => void
  } = $props()

  const resort = $derived(data.resort)
  const isOpen = $derived(resort.status === 'open')
  const f = $derived(filtering(filter))
  /** Closed with nothing turning: preseason, or the season over. */
  const closed = $derived(resort.status === 'closed' && m.turning === 0)
  /** Preseason only while opening day is ahead: the worker sends its date all year. */
  const hours = $derived(
    isOpen
      ? resort.opens && resort.closes
        ? `${hourText(resort.opens)} – ${hourText(resort.closes)}`
        : 'open'
      : opens
        ? 'preseason'
        : 'closed',
  )
  const fresh = $derived(freshnessParts(data.checkedAt, data.changedAt, now, quiet, live))
  const per = $derived(perArea(data.areas, filter))
  const lifts = $derived(liftsLine(m.lifts))
  const liftNames = $derived(
    lifts.names && liftsShown != null ? lifts.names.slice(0, liftsShown) : lifts.names,
  )
  /** The report's own note, as a headline: without its closing full stop. */
  const note = $derived(resort.note?.replace(/\.\s*$/, ''))
</script>

<header class:filtering={f} class:open-mode={openMode} class:lamps>
  <div class="trailhead"><svg viewBox="0 0 42 24" fill="none" aria-hidden="true"><path d="m1 22 13-19 10 14 6-9 11 14H1Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/><path d="m9 11 5 2 4-4M26 14l4 2 3-4" stroke="currentColor" stroke-width="1.5"/></svg><span>Lake Louise <b>Mountain report</b></span></div>
  <div class="clock">{clockTime(now)}<PersonalTools {data} /></div>
  <div class="hours" class:on={isOpen}><i aria-hidden="true"></i><span>{hours}</span></div>
  <!-- A narrow line drops the "changed" part whole, onto a second line that is never shown. -->
  <div class="fresh">
    <span class="lead">{fresh.lead}</span>{#if fresh.changed}<span class="chg"
        >{' '}<span aria-hidden="true">·</span> {fresh.changed}</span
      >{/if}
  </div>
  {#if f}
    <h1 class="long">{filterTitle(matched, filter)}</h1>
    <div class="sub">
      {#if per.length > 3}
        <span class="in">in {per.length} of {data.areas.length} areas</span>
      {:else if per.length}
        <span class="mono">In</span>
        <span class="in">
          {#each per as [name, n], i}{i ? ' · ' : ''}{name} <b>{n}</b>{/each}
        </span>
      {:else}
        <span class="in">none on the mountain</span>
      {/if}
      <button class="clear mono" onclick={onclear}>Clear</button>
    </div>
  {:else if closed && opens}
    <h1>{opens.title}</h1>
    <div class="sub">
      <span class="mono">Season</span>
      <span class="in">{opens.when} · {m.lifts.length} lifts · {m.runs.length} runs</span>
    </div>
  {:else if closed && !m.anyOpen}
    <h1 class:long={!!note}>{note || 'Closed'}</h1>
    <div class="sub">
      <span class="mono">Season</span>
      <span class="in">{m.lifts.length} lifts · {m.runs.length} runs</span>
    </div>
  {:else}
    <h1>{m.turning} <span class="of">of {m.lifts.length}</span> lifts turning</h1>
    <div class="sub">
      <span class="mono">Runs</span>
      <span><b>{m.open}</b> of {m.runs.length} open</span>
      <span>·</span>
      <Groomed />
      <span><b>{m.groomed}</b> groomed</span>
    </div>
  {/if}
  <div class="liftsx">
    <span class="mono">{lifts.label}</span>{#if liftNames && lifts.names}<b
        >{liftNames.join(' · ')}</b
      >{#if liftNames.length < lifts.names.length}{liftNames.length ? ' · ' : ''}<span
          class="more">+{lifts.names.length - liftNames.length} more</span
        >{/if}{:else}{lifts.text}{/if}
  </div>
</header>

<style>
  header {
    flex: none;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    column-gap: 12px;
  }
  .trailhead { grid-column: 1 / -1; display: flex; align-items: center; gap: 9px; margin-bottom: 14px; color: var(--ink); }
  .trailhead svg { width: 35px; height: 24px; }
  .trailhead span { display: flex; flex-direction: column; text-transform: uppercase; font-size: 16px; font-weight: 800; letter-spacing: .12em; line-height: 19px; }
  .trailhead b { font-size: 11px; font-weight: 500; letter-spacing: .15em; color: var(--ink-2); line-height: 15px; }
  .clock {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 15px;
    line-height: 20px;
  }
  .fresh {
    grid-column: 1 / -1;
    display: flex;
    flex-wrap: wrap;
    column-gap: 0.3em;
    height: 16px;
    overflow: hidden;
    font-size: 12px;
    line-height: 16px;
    color: var(--ink-2);
    white-space: nowrap;
  }
  .fresh .lead {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .hours {
    align-self: start;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    line-height: 20px;
    color: var(--ink-2);
    padding-top: 2px;
    white-space: nowrap;
  }
  .hours i {
    width: 11px;
    height: 11px;
    border-radius: 50%;
    border: 1.5px solid var(--ink-3);
    flex: none;
  }
  .hours.on i {
    border-color: var(--amber);
    background: var(--amber);
    box-shadow: 0 0 0 3px var(--amber-halo);
  }
  h1 {
    grid-column: 1 / -1;
    margin-top: 12px;
    font-size: 24px;
    font-weight: 650;
    line-height: 30px;
    letter-spacing: -0.005em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  h1 .of {
    color: var(--ink-2);
  }
  h1.long {
    font-size: 18px;
  }
  .sub {
    grid-column: 1 / -1;
    margin-top: 1px;
    min-height: 22px;
    font-size: 14px;
    line-height: 22px;
    color: var(--ink-2);
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
  }
  .sub .mono {
    color: var(--ink-2);
    flex: none;
  }
  .sub b {
    color: var(--ink);
    font-weight: 400;
  }
  /* 44 px tall in a 22 px line: it reaches over the headline, so it sits on top. */
  .sub .clear {
    position: relative;
    margin-left: auto;
    height: 44px;
    min-width: 44px;
    margin-block: -11px;
    padding: 0 2px 0 12px;
    color: var(--amber-ink);
    flex: none;
  }
  .sub .in {
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }
  .liftsx {
    display: none;
    grid-column: 1 / -1;
    font-size: 13px;
    line-height: 18px;
    color: var(--ink-2);
    white-space: nowrap;
    overflow: hidden;
  }
  .lamps:not(.filtering):not(.open-mode) .liftsx {
    display: block;
  }
  .liftsx .more {
    font: 400 11px/18px var(--mono);
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }
  .liftsx .mono {
    margin-right: 6px;
  }
  .liftsx b {
    color: var(--ink);
    font-weight: 400;
  }

  /* Short phones (375 × 667): closed lifts named here, lamps in the plates. */
  @media (max-width: 999px) and (max-height: 760px) {
    .trailhead { margin-bottom: 7px; }
    .trailhead span { font-size: 13px; line-height: 15px; }
    .trailhead svg { width: 29px; height: 21px; }
    .hours {
      padding-top: 0;
      font-size: 13px;
    }
    h1 {
      margin-top: 6px;
      font-size: 22px;
      line-height: 27px;
    }
    h1.long {
      font-size: 17px;
    }
    .sub {
      font-size: 13px;
      line-height: 20px;
      min-height: 20px;
    }
    .liftsx {
      display: block;
    }
    .open-mode:not(.filtering) h1,
    .open-mode:not(.filtering) .sub,
    .open-mode .liftsx,
    .filtering .liftsx {
      display: none;
    }
  }
  @media (max-width: 999px), (max-height: 619px) {
    .trailhead { display: none; }
    .open-mode:not(.filtering) h1, .open-mode:not(.filtering) .sub, .open-mode .liftsx { display: none; }
  }
  @media (min-width: 1000px) and (min-height: 620px) {
    header {
      grid-column: 1;
      grid-row: 1;
      grid-template-columns: 210px minmax(0, 1fr) 310px;
      grid-template-rows: 28px 22px 18px;
      column-gap: 20px;
    }
    .trailhead { grid-column: 1; grid-row: 1 / 4; margin-bottom: 0; align-self: center; }
    .trailhead span { font-size: 14px; }
    .trailhead b { font-size: 11px; letter-spacing: .1em; }
    .clock { grid-column: 3; grid-row: 1; }
    .hours { grid-column: 3; grid-row: 2; justify-self: end; padding: 0; }
    .fresh { grid-column: 3; grid-row: 3; justify-self: end; max-width: 100%; }
    h1 { grid-column: 2; grid-row: 1; margin: 0; font-size: 23px; line-height: 28px; }
    h1.long { font-size: 17px; }
    .sub { grid-column: 2; grid-row: 2; margin: 0; }
    .liftsx, .lamps:not(.filtering):not(.open-mode) .liftsx { display: block; grid-column: 2; grid-row: 3; font-size: 12px; }
  }
</style>
